import { AlertColor, Container } from '@mui/material';
import { CountryCode } from 'libphonenumber-js';
import dayjs from 'dayjs';
import { FC, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchFromStorage } from 'utils/storage';
import { createDropdownData } from 'utils/validators/HelperFunctions';
import strings from '../../../../common/Translation/Translate';
import axiosInstance from '../../../../utils/axios';
import { routes } from '../../../../utils/constants/routes';
import { URLS } from '../../../../utils/constants/urls';
import { defaultLanguage, Privilege } from '../../../../utils/redux/reducer/authentication-slice';
import AddEdit from '../../CommonPage/AddEdit';
import MasterDataEntityDrawer from '../../CommonPage/MasterDataEntityDrawer';
import { AddCustomerForm } from './AddCustomerForm';
import Notification from '../../../../components/Notification';
import PageLoader from '../../../../components/PageLoader';
import Form, { FormDataModel } from '../../../../components/Form';
import CustomerCompanyBranchMappings, { CustomerCompanyBranchMappingRow } from './CustomerCompanyBranchMappings';
import ExistingCustomerSearchSection from './ExistingCustomerSearchSection';
import { privileges } from '../../../../utils/constants/privileges';

interface AddEditCustomerProps {
  editingCustomerId?: number | string | null;
  companyId?: number;
  branchId?: number;
  isSalesManagementEnabled?: boolean;
  isCaptureLoyaltyProgram?: boolean;
  companyName?: string;
  handleSave?: () => void;
  /** When set with `open`, renders MasterDataEntityDrawer (Users-drawer style) instead of page AddEdit. */
  open?: boolean;
  onClose?: () => void;
  branchData?: {
    country?: number;
    state?: number;
    city?: number;
    address?: string;
    states?: any[];
    cities?: any[];
  };
}

const AddEditCustomer: FC<AddEditCustomerProps> = ({
  editingCustomerId,
  companyId,
  branchId,
  companyName,
  handleSave,
  open,
  onClose,
  branchData,
}) => {
  const Navigate = useNavigate();
  const isDrawerMode = typeof onClose === 'function';
  const drawerOpen = open ?? false;
  const userPrivileges = useSelector(Privilege) as string[];
  const defaultCountry = (fetchFromStorage('defaultCountryCode') || 'IN') as CountryCode;
  const isSalesManagementEnabled = fetchFromStorage('isSalesManagementEnabled') === 'true';
  const isCaptureLoyaltyProgram = fetchFromStorage('isCaptureLoyaltyProgram') === 'true';
  const selectedLanguage = useSelector(defaultLanguage);
  const { id } = useParams() as any;
  const customerId: any =
    editingCustomerId !== undefined && editingCustomerId !== null
      ? editingCustomerId
      : id && !companyId && !branchId
      ? id
      : null;
  const [pageResponse, setPageResponse] = useState<any>({});
  const [language, setLanguage] = useState<any>([]);
  const [country, setCountry] = useState<any>([]);
  const [states, setStates] = useState<any>([]);
  const [cities, setCities] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [company, setCompany] = useState<boolean>(false);
  const [companyMasterList, setcompanyMasterList] = useState<any>([]);
  const [branchesList, setBranchesList] = useState<any>([]);
  const [companyBranchMappings, setCompanyBranchMappings] = useState<CustomerCompanyBranchMappingRow[]>([]);
  const [branchesByCompany, setBranchesByCompany] = useState<Record<string, any[]>>({});
  const [addMembershipDetails, setAddMembershipDetails] = useState<boolean>(false);
  const [shouldAutoFocus, setShouldAutoFocus] = useState(false);
  const formRef: RefObject<Form | null | undefined> = useRef();
  const lastFetchedBranchRef = useRef<{ companyId: number | null; branchId: number | null }>({
    companyId: null,
    branchId: null,
  });
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const getDropdownValue = (value: any) => {
    if (value && typeof value === 'object') {
      return value.value ?? value.id ?? '';
    }
    return value ?? '';
  };

  const normalizeCompanyBranchMappings = useCallback(
    (data: any): CustomerCompanyBranchMappingRow[] => {
      const mappings = Array.isArray(data?.companyBranchMappings) ? data.companyBranchMappings : [];
      if (mappings.length > 0) {
        return mappings.map((mapping: any, index: number) => ({
          salesCompany: getDropdownValue(mapping.salesCompany),
          salesBranch: getDropdownValue(mapping.salesBranch),
          defaultMapping: !!mapping.defaultMapping || index === 0,
        }));
      }

      const salesCompany = getDropdownValue(data?.salesCompany) || companyId || '';
      const salesBranch = getDropdownValue(data?.salesBranch) || branchId || '';
      return salesCompany && salesBranch
        ? [
            {
              salesCompany,
              salesBranch,
              defaultMapping: true,
            },
          ]
        : [];
    },
    [branchId, companyId],
  );

  const fetchBranchesForCompanyMapping = useCallback(async (companyMasterId: number | string) => {
    if (!companyMasterId) {
      return;
    }
    try {
      const { data } = await axiosInstance(URLS.getCompanyBranchesDropdown(companyMasterId));
      setBranchesByCompany(prevState => ({
        ...prevState,
        [String(companyMasterId)]: createDropdownData(data, ['id', 'name']),
      }));
    } catch (error: any) {
      handleCatch(error);
    }
  }, []);

  const getInitialResponse = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.getUserById(customerId));

      const isCompany = companyId ? true : !!data?.company;
      setCompany(isCompany);

      const setNewData = {
        ...data,
        company: companyId ? 'Yes' : data.company ? 'Yes' : 'No',
        language: data?.language,
        city: data?.city?.value,
        state: data?.state?.value,
        country: data?.country?.value,
        salesCompany: data?.salesCompany?.value || companyId,
        salesBranch: data?.salesBranch?.value || branchId,
        companyName: companyName,
        birthDate: data?.birthDate || '',
        anniversaryDate: data?.anniversaryDate || '',
      };
      const normalizedMappings = normalizeCompanyBranchMappings(data);
      setCompanyBranchMappings(normalizedMappings);
      await Promise.all(
        Array.from(new Set(normalizedMappings.map(mapping => mapping.salesCompany).filter(Boolean))).map(companyId =>
          fetchBranchesForCompanyMapping(companyId),
        ),
      );

      if (data?.country?.value) {
        await getState(data?.country?.value);
      }
      if (data?.state?.value) {
        await getCities(data?.state?.value);
      }
      if (isCompany && isSalesManagementEnabled) {
        if (companyId) {
          await getBranches(companyId);
        } else if (data?.salesCompany?.value) {
          await getBranches(data?.salesCompany?.value);
        }
      }
      // Merge membership details into pageResponse if available
      if (isCaptureLoyaltyProgram) {
        const membershipData = await getMembershipDetails();
        const hasMembershipData = Object.values(membershipData).some((val: any) => val !== '' && val !== null);
        setAddMembershipDetails(hasMembershipData);
        setPageResponse({
          ...setNewData,
          ...membershipData,
          addMembershipDetails: hasMembershipData,
        });
      } else {
        setPageResponse(setNewData);
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, fetchBranchesForCompanyMapping, normalizeCompanyBranchMappings]);

  const getMembershipDetails = useCallback(async () => {
    if (!customerId) return {};
    try {
      const { data } = await axiosInstance.get(URLS.getMembershipDetails(customerId));
      if (data) {
        return {
          memberName: data.memberName || '',
          memberType: data.memberType || '',
          loyaltyReference: data.loyaltyReference || '',
          startDate: data.startDate || '',
          endDate: data.endDate || '',
          status: data.status || '',
        };
      }
      return {};
    } catch (error: any) {
      // If membership details don't exist, that's okay - user might not have them yet
      if (error.response?.status !== 404) {
        console.error('Error fetching membership details:', error);
      }
      return {};
    }
  }, [customerId]);

  const getLanguage = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.language);
      const filter = data.map((item: any) => {
        return { name: item.text, id: item.text };
      });
      setLanguage(filter);
      setPageResponse((prevState: any) => ({
        ...prevState,
        language: customerId ? prevState.language : { label: filter[0]?.name, value: filter[0]?.id },
        // company: customerId && data.company ? 'Yes' : 'No',
      }));
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  const getCountry = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.countries);
      const filter = data.map((item: any) => {
        return { label: item.name, value: item.id };
      });
      setCountry(filter);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getState = useCallback(async (id: number, options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) setLoading(true);
      const { data } = await axiosInstance.get(URLS.states(id));
      const filter = data.map((item: any) => {
        return { label: item.name, value: item.id };
      });
      setStates(filter);
    } catch {
    } finally {
      if (!options?.silent) setLoading(false);
    }
  }, []);

  const getCities = useCallback(async (id: number, options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) setLoading(true);
      const { data } = await axiosInstance.get(URLS.cities(id));
      const filter = data.map((item: any) => {
        return { label: item.name, value: item.id };
      });
      setCities(filter);
    } catch {
    } finally {
      if (!options?.silent) setLoading(false);
    }
  }, []);

  const getCompanyMasters = useCallback(async () => {
    try {
      const { status, data } = await axiosInstance.get(URLS.getCompanyDropdownBooking);
      if (status === 200) {
        setcompanyMasterList(createDropdownData(data, ['id', 'name']));
      }
    } catch (error: any) {
      handleCatch(error);
    }
  }, []);

  const getBranches = useCallback(
    async (companyMasterId: number) => {
      try {
        setLoading(true);
        const { data } = await axiosInstance(URLS.getCompanyBranchesDropdown(companyMasterId));

        if (data.length === 0) {
          setMessage({
            display: true,
            severity: 'error',
            message: 'please add branch first',
          });
        }

        setBranchesList(createDropdownData(data, ['id', 'name']));
        if (companyId) {
          setPageResponse((prevState: any) => ({
            ...prevState,
            salesCompany: companyId,
            salesBranch: branchId,
          }));
        } else {
          setPageResponse((prevState: any) => ({
            ...prevState,
            salesCompany: companyMasterId,
            salesBranch: '',
          }));
        }
      } catch (error: any) {
        handleCatch(error);
      } finally {
        setLoading(false);
      }
    },
    [branchId, companyId],
  );

  const fetchBranchDetailsAndPopulateLocation = useCallback(
    async (companyIdValue: number, branchIdValue: number) => {
      if (
        lastFetchedBranchRef.current.companyId === companyIdValue &&
        lastFetchedBranchRef.current.branchId === branchIdValue
      ) {
        return;
      }

      try {
        setLoading(true);
        const { data } = await axiosInstance.get(URLS.getBranchDetailsById(companyIdValue, branchIdValue));

        if (data) {
          const branchCountry = data?.country?.value || data?.country;
          const branchState = data?.state?.value || data?.state;
          const branchCity = data?.city?.value || data?.city;
          if (branchCountry) {
            await getState(branchCountry);

            if (branchState) {
              await getCities(branchState);
            }
          }

          setPageResponse((prevState: any) => ({
            ...prevState,
            country: branchCountry || prevState.country,
            state: branchState || prevState.state,
            city: branchCity || prevState.city,
            address: data?.branchAddress || data?.address || prevState.address,
          }));

          lastFetchedBranchRef.current = {
            companyId: companyIdValue,
            branchId: branchIdValue,
          };
        }
      } catch (error: any) {
        console.error('Error fetching branch details:', error);
      } finally {
        setLoading(false);
      }
    },
    [getState, getCities],
  );

  useEffect(() => {
    getLanguage();
    getCountry();
    if (isSalesManagementEnabled) {
      getCompanyMasters();
    }
  }, [getCountry, getLanguage, getCompanyMasters, isSalesManagementEnabled]);

  useEffect(() => {
    (async () => {
      if (isDrawerMode && !drawerOpen) {
        return;
      }
      if (customerId) {
        await getInitialResponse();
      } else {
        const isCompany = !!companyId;
        setCompany(isCompany);
        const formData: any = {
          company: companyId ? 'Yes' : 'No',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          pinCode: '',
          address: branchData?.address || '',
          designation: '',
          salesCompany: companyId,
          salesBranch: branchId,
          companyName: companyName,
          gst: '',
          language: '',
          country: branchData?.country || '',
          state: branchData?.state || '',
          city: branchData?.city || '',
          birthDate: '',
          anniversaryDate: '',
        };
        // Add membership fields if loyalty program is enabled
        if (isCaptureLoyaltyProgram) {
          formData.addMembershipDetails = false;
          formData.memberName = '';
          formData.memberType = '';
          formData.loyaltyReference = '';
          formData.startDate = '';
          formData.endDate = '';
        }
        setPageResponse(formData);
        const initialMappings =
          companyId && branchId
            ? [
                {
                  salesCompany: companyId,
                  salesBranch: branchId,
                  defaultMapping: true,
                },
              ]
            : [];
        setCompanyBranchMappings(initialMappings);
        if (companyId) {
          await fetchBranchesForCompanyMapping(companyId);
        }

        // If branchData is provided, use existing states and cities arrays if available
        if (branchData?.country) {
          if (branchData?.states && branchData.states.length > 0) {
            // Use existing states from branch
            setStates(branchData.states);
          } else {
            // Fetch states if not provided
            await getState(branchData.country);
          }

          if (branchData?.state) {
            if (branchData?.cities && branchData.cities.length > 0) {
              // Use existing cities from branch
              setCities(branchData.cities);
            } else {
              // Fetch cities if not provided
              await getCities(branchData.state);
            }
          }
        }

        if (companyId && branchId && isSalesManagementEnabled) {
          await fetchBranchDetailsAndPopulateLocation(companyId, branchId);
        }
      }
    })();
  }, [
    branchId,
    companyId,
    customerId,
    drawerOpen,
    editingCustomerId,
    getInitialResponse,
    id,
    isDrawerMode,
    selectedLanguage.value,
    branchData,
    isSalesManagementEnabled,
    fetchBranchDetailsAndPopulateLocation,
    fetchBranchesForCompanyMapping,
    companyName,
    isCaptureLoyaltyProgram,
    getState,
    getCities,
  ]);

  useEffect(() => {
    (async () => {
      if (companyId) {
        await getBranches(companyId);
      }
    })();
  }, [companyId, getBranches]);

  useEffect(() => {
    const defaultMapping = companyBranchMappings.find(mapping => mapping.defaultMapping) || companyBranchMappings[0];
    if (!customerId && isSalesManagementEnabled && defaultMapping?.salesCompany && defaultMapping?.salesBranch) {
      fetchBranchDetailsAndPopulateLocation(Number(defaultMapping.salesCompany), Number(defaultMapping.salesBranch));
    }
  }, [companyBranchMappings, customerId, fetchBranchDetailsAndPopulateLocation, isSalesManagementEnabled]);

  const companyBranchMappingSection = useMemo(
    () =>
      company && isSalesManagementEnabled ? (
        <CustomerCompanyBranchMappings
          mappings={companyBranchMappings}
          companies={companyMasterList}
          branchesByCompany={branchesByCompany}
          disabledCompanyId={companyId}
          disabledBranchId={branchId}
          onCompanyChange={fetchBranchesForCompanyMapping}
          onChange={setCompanyBranchMappings}
        />
      ) : null,
    [
      branchId,
      branchesByCompany,
      company,
      companyBranchMappings,
      companyId,
      companyMasterList,
      fetchBranchesForCompanyMapping,
      isSalesManagementEnabled,
    ],
  );

  // One-time autofocus on add; clear quickly so country/state reloads don't jump to firstName
  useEffect(() => {
    if (isDrawerMode && !drawerOpen) return;
    setShouldAutoFocus(!customerId);
    const t = window.setTimeout(() => setShouldAutoFocus(false), 400);
    return () => window.clearTimeout(t);
  }, [isDrawerMode, drawerOpen, customerId]);

  // Build form model conditionally based on checkbox state
  const formModel = useMemo(
    () =>
      AddCustomerForm({
        strings,
        country,
        states,
        cities,
        company,
        defaultCountry,
        salesCompanies: companyMasterList,
        salesBranches: branchesList,
        companyId,
        isSalesManagementEnabled,
        defaultCompanyValue: pageResponse?.company || (companyId ? 'Yes' : ''), // Pass company value from pageResponse state
        isCaptureLoyaltyProgram,
        showMembershipFields: addMembershipDetails || pageResponse?.addMembershipDetails,
        isEditMode: !!customerId, // Pass edit mode flag
        enableCompanyBranchMappings: isSalesManagementEnabled,
        companyBranchMappingSection,
        autoFocusFirstName: shouldAutoFocus,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      country,
      states,
      cities,
      company,
      defaultCountry,
      companyMasterList,
      branchesList,
      companyId,
      isSalesManagementEnabled,
      pageResponse?.company,
      isCaptureLoyaltyProgram,
      addMembershipDetails,
      pageResponse?.addMembershipDetails,
      customerId, // Include customerId to update form when switching between add/edit
      companyBranchMappingSection,
      shouldAutoFocus,
    ],
  );

  const pageConfig = {
    title: customerId ? strings.customermgmt_title_edit : strings.customermgmt_title_add,
    successMessage: customerId ? strings.common_update_message : strings.common_save_message,
    addEditApiUrl: customerId ? URLS.editCustomer(customerId) : URLS.addCustomer,
    initialApi: URLS.editCustomer(customerId),
    hideClose: Boolean(companyId) || isDrawerMode,
    customMethod: customerId ? undefined : 'post',
    formModel,
    navigateToPage: companyId || isDrawerMode ? undefined : routes.customerList,
    isCustomResponse: true,
    buttonText: customerId ? strings.customermgmt_title_edit : strings.customermgmt_title_add,
  };

  /** Restore focus on the field the user just changed (don't jump to top / next field). */
  const keepFocusOnField = (field: string) => {
    window.setTimeout(() => {
      const root = document.getElementById(field);
      const input =
        (root?.querySelector?.('input') as HTMLInputElement | null) || (root as HTMLInputElement | null);
      input?.focus?.();
    }, 50);
  };

  const handlePropsChange = async (key: string, value: any, formData: any) => {
    if (key === 'country') {
      setShouldAutoFocus(false);
      setCities([]);
      setPageResponse({ ...formData, id: pageResponse.id, state: '', city: '', country: value });
      await getState(value, { silent: true });
      keepFocusOnField('country');
      return;
    }

    if (key === 'state') {
      setShouldAutoFocus(false);
      setPageResponse({ ...formData, id: pageResponse.id, city: '', state: value });
      await getCities(value, { silent: true });
      keepFocusOnField('state');
      return;
    }

    if (key === 'company') {
      // Force company to "Yes" when companyId is present
      const companyValue = companyId ? 'Yes' : value;
      if (companyId) {
        setPageResponse({
          ...formData,
          id: pageResponse.id,
          company: 'Yes', // Force to "Yes" when companyId is present
          salesCompany: companyId,
          salesBranch: branchId,
          companyName: formData?.companyName || companyName,
        });
        setCompany(true);
      } else {
        setBranchesList([]);
        setCompanyBranchMappings(
          companyValue === 'Yes' && companyId && branchId
            ? [{ salesCompany: companyId, salesBranch: branchId, defaultMapping: true }]
            : [],
        );
        setPageResponse({ ...formData, id: pageResponse.id, company: companyValue, salesCompany: '', salesBranch: '' });
        setCompany(companyValue === 'Yes');
      }
    }

    if (key === 'salesCompany') {
      const companyNameId = typeof value === 'object' ? value.value : value;
      getBranches(companyNameId);
      setPageResponse({ ...formData, id: pageResponse.id, salesCompany: value, salesBranch: '' });
      lastFetchedBranchRef.current = { companyId: null, branchId: null };
    }

    if (key === 'salesBranch') {
      const branchIdValue = typeof value === 'object' ? value.value : value;
      const companyIdValue = formData?.salesCompany
        ? typeof formData.salesCompany === 'object'
          ? formData.salesCompany.value
          : formData.salesCompany
        : companyId;

      setPageResponse({ ...formData, id: pageResponse.id, salesBranch: value });

      if (companyIdValue && branchIdValue && isSalesManagementEnabled) {
        fetchBranchDetailsAndPopulateLocation(companyIdValue, branchIdValue);
      }
    }

    if (key === 'addMembershipDetails') {
      setAddMembershipDetails(value);
      setPageResponse({ ...formData, id: pageResponse.id, addMembershipDetails: value });
      // If unchecked, clear membership fields (but not birthDate and anniversaryDate as they are customer fields)
      if (!value) {
        const clearedData = {
          ...formData,
          id: pageResponse.id,
          addMembershipDetails: false,
          memberName: '',
          memberType: '',
          loyaltyReference: '',
          startDate: '',
          endDate: '',
        };
        setPageResponse(clearedData);
      }
    }
  };
  const handleCustomResponse = (custom: any) => {
    const city = cities.find((el: any) => el.value === custom.city);
    const countries = country.find((el: any) => el.value === custom.country);
    const state = states.find((el: any) => el.value === custom.state);

    if (custom) {
      const companyValue = companyId ? true : custom.company === 'Yes';
      const hasDefaultMapping = companyBranchMappings.some(mapping => mapping.defaultMapping);
      const hasCompanyWithoutBranch = companyBranchMappings.some(mapping => mapping.salesCompany && !mapping.salesBranch);
      const uniqueMappingKeys = new Set<string>();
      const validMappings = companyBranchMappings
        .filter(mapping => mapping.salesCompany && mapping.salesBranch)
        .filter(mapping => {
          const key = `${mapping.salesCompany}-${mapping.salesBranch}`;
          if (uniqueMappingKeys.has(key)) return false;
          uniqueMappingKeys.add(key);
          return true;
        })
        .map((mapping, index) => ({
          salesCompany: Number(mapping.salesCompany),
          salesBranch: Number(mapping.salesBranch),
          defaultMapping: !!mapping.defaultMapping || (!hasDefaultMapping && index === 0),
        }));
      const defaultMapping = validMappings.find(mapping => mapping.defaultMapping) || validMappings[0];

      if (companyValue && isSalesManagementEnabled && hasCompanyWithoutBranch) {
        setMessage({
          display: true,
          severity: 'error',
          message: 'Please select a branch for the selected company mapping.',
        });
        return false;
      }

      // Remove membership fields from customer data (they will be saved separately)
      // Note: birthDate and anniversaryDate are now customer fields, not membership fields
      const {
        memberName,
        memberType,
        loyaltyReference,
        startDate,
        endDate,
        status,
        companyBranchMappingSection,
        ...customerData
      } = custom;

      return {
        ...customerData,
        country: countries?.value || null,
        state: state?.value || null,
        city: city?.value || null,
        salesBranch: defaultMapping?.salesBranch || (companyId ? branchId : null),
        salesCompany: defaultMapping?.salesCompany || (companyId ? companyId : null),
        companyBranchMappings: validMappings,
        // Only include id if we're editing (customerId exists)
        ...(customerId && { id: customerId }),
        language: custom?.language?.value || '',
        company: companyValue,
        role: 'ROLE_CUSTOMER',
        pinCode: custom.pinCode === '' ? null : custom.pinCode,
        birthDate: custom.birthDate || null,
        anniversaryDate: custom.anniversaryDate || null,
      };
    }
  };

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error.response.data?.error_description ?? error.response.data.message ?? error.message,
    });
  };

  const saveMembershipDetails = async (customerId: number | string, membershipData: any) => {
    if (!isCaptureLoyaltyProgram) return true;

    const startDate = membershipData.startDate || '';
    const endDate = membershipData.endDate || '';

    // Validate dates if both are provided
    if (startDate && endDate && dayjs(startDate).isValid() && dayjs(endDate).isValid()) {
      if (dayjs(startDate).isAfter(dayjs(endDate))) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.common_endDate_error_message || 'Start date must be less than or equal to end date.',
        });
        return false;
      }
    }

    // Only save if at least one membership field has a value
    const hasMembershipData =
      membershipData.memberName ||
      membershipData.memberType ||
      membershipData.loyaltyReference ||
      membershipData.startDate ||
      membershipData.endDate;

    if (!hasMembershipData) {
      return true; // No membership data to save, but that's okay
    }

    try {
      const requestData = {
        memberName: membershipData.memberName || '',
        memberType: membershipData.memberType || null,
        loyaltyReference: membershipData.loyaltyReference || '',
        startDate: membershipData.startDate || null,
        endDate: membershipData.endDate || null,
        status: membershipData.status || null,
      };
      await axiosInstance.post(URLS.addMembershipDetails(customerId), requestData);
      return true;
    } catch (error: any) {
      handleCatch(error);
      return false;
    }
  };
  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const attachExistingCustomerToBranch = async (existingCustomerId: number | string) => {
    if (!companyId || !branchId) return;
    try {
      setLoading(true);
      await axiosInstance.post(URLS.addCustomerCompanyBranchMapping(existingCustomerId), {
        salesCompany: companyId,
        salesBranch: branchId,
        defaultMapping: false,
      });
      setMessage({
        display: true,
        severity: 'success',
        message: strings.common_save_message,
      });
      handleSave?.();
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  const existingCustomerSearch =
    !customerId && companyId && branchId ? (
      <ExistingCustomerSearchSection
        companyId={companyId}
        branchId={branchId}
        loading={loading}
        onAttachCustomer={attachExistingCustomerToBranch}
        onError={handleCatch}
      />
    ) : null;

  const handleCustomCallbackResponse = async (data: any, membershipSource?: FormDataModel) => {
    const customerIdToUse = data?.id || customerId;

    // If loyalty program is enabled and checkbox is checked, save membership details
    if (isCaptureLoyaltyProgram && customerIdToUse) {
      let formData = membershipSource;
      if (!formData && formRef.current) {
        const { getFormData } = formRef.current as {
          getFormData: () => { formData: FormDataModel; isFormValid: boolean };
        };
        formData = getFormData().formData;
      }

      // Only save membership if checkbox is checked
      if (formData?.addMembershipDetails) {
        const membershipData = {
          memberName: formData.memberName || '',
          memberType: formData.memberType || '',
          loyaltyReference: formData.loyaltyReference || '',
          startDate: formData.startDate || '',
          endDate: formData.endDate || '',
          status: formData.status || '',
        };

        const membershipSaved = await saveMembershipDetails(customerIdToUse, membershipData);
        if (!membershipSaved) {
          return; // Don't navigate if membership save failed
        }
      }
    }

    if (handleSave) {
      handleSave();
    } else if (companyId) {
      // Company branch embedded context without explicit handleSave
    } else {
      Navigate(routes.customerList);
    }
  };

  const handleDrawerSave = async (body: FormDataModel) => {
    try {
      setSaving(true);
      const payload = handleCustomResponse(body);
      if (payload === false) {
        return;
      }
      const response = customerId
        ? await axiosInstance.put(URLS.editCustomer(customerId), payload)
        : await axiosInstance.post(URLS.addCustomer, payload);
      await handleCustomCallbackResponse(response?.data, body);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  if (isDrawerMode) {
    return (
      <>
        {message.display && (
          <Notification
            isOpen={message.display}
            message={message.message}
            severity={message.severity as AlertColor}
            closeNotification={closeNotification}
          />
        )}
        <MasterDataEntityDrawer
          open={drawerOpen}
          title={customerId ? strings.customermgmt_title_edit : strings.customermgmt_title_add}
          isEdit={Boolean(customerId)}
          formModel={formModel}
          formValues={pageResponse}
          dropdownData={{
            language: createDropdownData(language, ['id', 'name']),
          }}
          onFieldChange={handlePropsChange}
          loading={loading}
          saving={saving}
          canDelete={Boolean(customerId) && userPrivileges?.includes(privileges.delete_customer)}
          width="70%"
          onClose={onClose!}
          onSave={handleDrawerSave}
          onDelete={
            customerId
              ? async () => {
                  try {
                    setSaving(true);
                    await axiosInstance.delete(URLS.deleteCustomer(String(customerId)));
                    setMessage({
                      display: true,
                      severity: 'success',
                      message: strings.common_delete_message || 'Deleted successfully',
                    });
                    handleSave?.();
                  } catch (error: any) {
                    handleCatch(error);
                  } finally {
                    setSaving(false);
                  }
                }
              : undefined
          }
          onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
          deleteEntityName={
            `${pageResponse.firstName || ''} ${pageResponse.lastName || ''}`.trim() ||
            String(pageResponse.email || '')
          }
          saveLabel={customerId ? 'Save Changes' : 'Save'}>
          {existingCustomerSearch}
        </MasterDataEntityDrawer>
      </>
    );
  }

  return (
    <Container className="content p-0" maxWidth={false}>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      {loading && <PageLoader />}
      <AddEdit
        pageLoading={loading}
        dropdownValue={{
          language: createDropdownData(language, ['id', 'name']),
        }}
        customCallbackResponse={handleCustomCallbackResponse}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        handlePropsChange={handlePropsChange}
        handleCustomResponse={handleCustomResponse}
        externalFormRef={formRef}
        beforeForm={existingCustomerSearch}
      />
    </Container>
  );
};
export default AddEditCustomer;
